import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ForecastMarkdown } from './ForecastMarkdown';

const ToggleButton: React.FC<{ collapsed: boolean; onClick: () => void }> = ({ collapsed, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: 'none',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      color: '#1890ff',
      fontSize: '12px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
    }}
  >
    {collapsed ? 'Show more' : 'Show less'}
  </button>
);

// ---------------------------------------------------------------------------
// Component: CollapsibleText
// Self-contained version that renders the markdown content.
//
// When `header` is provided, it renders a title row (header + toggle) above
// the collapsible content. When omitted, the toggle appears below the text.
// ---------------------------------------------------------------------------

interface CollapsibleTextProps {
  content: string;
  isDark: boolean;
  compact?: boolean;
  defaultCollapsed?: boolean;
  maxCollapsedLines?: number;
  maxWidth?: string | number;
  /** Override gradient background RGB (e.g. '255, 255, 255' for #ffffff). Auto-detected from isDark when omitted. */
  gradientRgb?: string;
  /** Optional header node (e.g. title + edit button). When provided, the show more/less toggle is placed in the header row. */
  header?: React.ReactNode;
}

export const CollapsibleText: React.FC<CollapsibleTextProps> = ({
  content,
  isDark,
  compact = false,
  defaultCollapsed = true,
  maxCollapsedLines = 1,
  maxWidth,
  gradientRgb,
  header,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const collapsedMaxHeight = `${maxCollapsedLines * 1.6}em`;

  const checkOverflow = useCallback(() => {
    const measure = measureRef.current;
    const visible = contentRef.current;
    if (!measure || !visible) return;
    setIsOverflowing(measure.scrollHeight > visible.clientHeight + 2);
  }, []);

  useEffect(() => {
    if (!collapsed) {
      setIsOverflowing(true);
      return;
    }
    checkOverflow();
  }, [content, collapsed, checkOverflow]);

  useEffect(() => {
    const id = requestAnimationFrame(checkOverflow);
    return () => cancelAnimationFrame(id);
  }, [checkOverflow]);

  const gradientColor = gradientRgb ?? (isDark ? '31, 31, 31' : '250, 250, 250');

  const toggle = isOverflowing ? (
    <ToggleButton collapsed={collapsed} onClick={() => setCollapsed(prev => !prev)} />
  ) : null;

  return (
    <div style={{ maxWidth: maxWidth ?? '1000px', wordWrap: 'break-word', position: 'relative' }}>
      {/* Header row with toggle */}
      {header && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
          {header}
          {toggle}
        </div>
      )}

      {/* Hidden full-height copy for measuring overflow */}
      <div
        ref={measureRef}
        aria-hidden
        style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', width: '100%' }}
      >
        <ForecastMarkdown source={content} isDark={isDark} compact={compact} />
      </div>

      <div
        ref={contentRef}
        style={{
          maxHeight: collapsed ? collapsedMaxHeight : undefined,
          overflow: collapsed ? 'hidden' : undefined,
          position: 'relative',
        }}
      >
        <ForecastMarkdown source={content} isDark={isDark} compact={compact} />
        {collapsed && isOverflowing && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '1.4em',
              background: `linear-gradient(transparent, rgba(${gradientColor}, 0.95))`,
              pointerEvents: 'none',
            }}
          />
        )}
      </div>

      {/* Inline toggle below text (only when no header) */}
      {!header && toggle}
    </div>
  );
};
